import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../../../../redux/store";
import Dropdown from "../../../../../components/Dropdown/Dropdown";
import PlusFilledIcon from "../../../../../assets/svg/plus-filled-icon";
import CompletedIcon from "../../../../../assets/svg/completed-icon";
import ChevronIcon from "../../../../../assets/svg/chevron-icon";
import EditIcon from "../../../../../assets/svg/edit-icon";
import CrossIcon from "../../../../../assets/svg/v1/cross.icon";
import { CrmViewSelectorDropdown } from "../../KanbanGrid/components/CrmViewSelectorDropdown";
import { setDrawer } from "../../../../../redux/slices/drawerSlice";
import { DEAL_PIPELINES, DEAL_LABELS } from "../utils/constants";
import { useGetDealsMetricsQuery } from "../../../../../redux/services/dealsApi";
import type { DealsActionBarProps } from "../utils/types";

export const DealsActionBar = ({
  onAddViewClick,
  dealsViews,
  selectedViewId: selectedViewIdProp,
  onSelectView,
  onDeleteView,
}: DealsActionBarProps) => {
  const dispatch = useDispatch();
  const [selectedPipeline, setSelectedPipeline] = useState(
    DEAL_PIPELINES[0]?.value || "concurrent_pipeline"
  );
  const [showMetrics, setShowMetrics] = useState(true);

  const selectedViewId = selectedViewIdProp ?? "all_deals";
  const metricsViewId =
    selectedViewId !== "all_deals" ? selectedViewId : undefined;
  const viewRefetchTrigger = useSelector(
    (state: RootState) => state.deals.viewRefetchTrigger
  );
  const {
    data: metricsData,
    isFetching: isMetricsLoading,
    refetch: refetchMetrics,
  } = useGetDealsMetricsQuery(
    metricsViewId ? { view_id: metricsViewId } : undefined
  );

  useEffect(() => {
    if (viewRefetchTrigger > 0) refetchMetrics();
  }, [viewRefetchTrigger, refetchMetrics]);

  const viewTabs = useMemo(() => {
    const realTabs = (dealsViews ?? []).map((v) => ({
      id: v.id,
      label: v.name,
      emoji_icon: v.emoji_icon,
      isDefault: false,
    }));
    return [
      ...[
        {
          id: "all_deals",
          label: DEAL_LABELS.allItems,
          isDefault: true,
          emoji_icon: undefined,
        },
      ],
      ...realTabs,
    ];
  }, [dealsViews]);

  return (
    <div className="flex flex-col gap-[16px] shrink-0">
      <div className="flex items-center gap-[18px]">
        <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
          <Dropdown
            options={DEAL_PIPELINES.map((p) => ({
              value: p.value,
              content: (
                <span className="text-md text-verba-indigo dark:text-verba-indigo-dk">
                  {p.content}
                </span>
              ),
            }))}
            value={selectedPipeline}
            onChange={(value) => setSelectedPipeline(value)}
            dropdownClassName="!w-fit"
            triggerClassName="w-[145px] h-[26px] rounded-[8px] whitespace-nowrap"
            selectedOptionContainerClassName="!py-0 h-full"
          />

          <div className="w-px h-[16px] bg-border-gray dark:bg-border-gray-dk shrink-0" />

          <div className="flex items-center gap-[8px] min-w-0 overflow-x-auto overflow-y-hidden no-scrollbar">
            {viewTabs.map((tab) => {
              const isActive = selectedViewId === tab.id;
              const isCustomView = !tab.isDefault;

              return (
                <button
                  key={tab.id}
                  onClick={() => onSelectView?.(tab.id)}
                  className={`group h-[26px] px-[6px] rounded-[6px] border flex items-center whitespace-nowrap cursor-pointer transition-all duration-150 shrink-0 ${
                    isActive
                      ? "gap-2 bg-crm-card-highlight-2 dark:bg-crm-card-highlight-2-dk border-crm-card-border dark:border-crm-card-border-dk"
                      : "gap-[2px] hover:gap-2 bg-transparent border-border-gray dark:border-border-gray-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                  }`}
                >
                  <div className="flex items-center gap-1">
                    {tab.isDefault && (
                      <CompletedIcon className="w-[14px] h-[14px] shrink-0 text-verba-blue dark:text-verba-blue-dk" />
                    )}
                    {tab.emoji_icon && (
                      <span className="text-md leading-none shrink-0">
                        {tab.emoji_icon}
                      </span>
                    )}
                    <span className="text-sm font-normal text-verba-indigo dark:text-verba-indigo-dk leading-[18px]">
                      {tab.label}
                    </span>
                  </div>

                  {isCustomView && (
                    <div
                      className={`flex items-center shrink-0 transition-[gap] duration-150 ${
                        isActive ? "gap-1" : "gap-0 group-hover:gap-1"
                      }`}
                    >
                      <span
                        className={`h-[14px] min-w-0 overflow-hidden shrink-0 flex items-center justify-center transition-[width,opacity] duration-150 mb-px ${
                          isActive
                            ? "w-[14px] opacity-100 pointer-events-auto"
                            : "w-0 opacity-0 pointer-events-none group-hover:w-[14px] group-hover:opacity-100 group-hover:pointer-events-auto"
                        }`}
                      >
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(
                              setDrawer({
                                open: true,
                                type: "deals_edit_view",
                                id: tab.id,
                              })
                            );
                          }}
                          className="flex items-center justify-center p-0 border-0 bg-transparent cursor-pointer"
                        >
                          <EditIcon className="w-[14px] h-[14px] shrink-0 text-dark-gray dark:text-dark-gray-dk" />
                        </span>
                      </span>

                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteView?.({
                            id: tab.id,
                            name: tab.label,
                            emoji_icon: tab.emoji_icon,
                          });
                        }}
                        className={`cursor-pointer h-[10px] min-w-0 overflow-hidden shrink-0 flex items-center justify-center rounded-full bg-dark-gray dark:bg-dark-gray-dk transition-[width,opacity] duration-150 ${
                          isActive
                            ? "w-[10px] opacity-100 pointer-events-auto"
                            : "w-0 opacity-0 pointer-events-none group-hover:w-[10px] group-hover:opacity-100 group-hover:pointer-events-auto"
                        }`}
                      >
                        <CrossIcon className="w-[6px] h-[6px] shrink-0 text-pure-white dark:text-pure-white-dk stroke-3" />
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onAddViewClick}
            className="flex items-center gap-1 text-md font-medium text-verba-blue dark:text-verba-blue-dk whitespace-nowrap hover:underline cursor-pointer"
          >
            <PlusFilledIcon className="w-[16px] h-[16px] text-verba-blue dark:text-verba-blue-dk" />
            Add a View
          </button>
          <div className="w-px h-[16px] bg-border-gray dark:bg-border-gray-dk shrink-0" />
          <CrmViewSelectorDropdown
            crmViews={viewTabs.map((v) => ({
              id: v.id,
              name: v.label,
              emoji_icon: v.emoji_icon,
            }))}
            selectedViewId={selectedViewId}
            onSelectView={(id) => onSelectView?.(id)}
            onCreateView={() =>
              dispatch(setDrawer({ open: true, type: "deals_view" }))
            }
          />
          <div className="w-px h-[16px] bg-border-gray dark:bg-border-gray-dk shrink-0" />
          <button
            onClick={() => setShowMetrics((prev) => !prev)}
            className="flex items-center gap-[4px] shrink-0 cursor-pointer"
          >
            <ChevronIcon
              className={`w-[14px] h-[14px] text-verba-blue dark:text-verba-blue-dk transition-transform ${
                showMetrics ? "" : "rotate-180"
              }`}
            />
            <span className="text-md font-medium text-verba-blue dark:text-verba-blue-dk whitespace-nowrap">
              {showMetrics ? "Hide Metrics" : "Show Metrics"}
            </span>
          </button>
        </div>
      </div>

      {showMetrics && (
        <div className="w-full border border-border-gray dark:border-border-gray-dk rounded-[8px] bg-pure-white dark:bg-pure-white-dk px-[24px] py-[18px]">
          <div className="flex items-start justify-between">
            {isMetricsLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex flex-col gap-[5px] items-center justify-center"
                  >
                    <div className="flex flex-col gap-[7px] items-center">
                      <div className="h-[12px] w-[80px] rounded bg-gradient-to-r from-[var(--color-gray-75)] to-[var(--color-gray-100)] dark:from-[var(--color-gray-75-dk)] dark:to-[var(--color-gray-100-dk)] animate-pulse" />
                      <div className="h-[30px] w-[100px] rounded bg-gradient-to-r from-[var(--color-gray-75)] to-[var(--color-gray-100)] dark:from-[var(--color-gray-75-dk)] dark:to-[var(--color-gray-100-dk)] animate-pulse" />
                    </div>
                    <div className="flex flex-col gap-[2px] items-center">
                      <div className="h-[14px] w-[90px] rounded bg-gradient-to-r from-[var(--color-gray-75)] to-[var(--color-gray-100)] dark:from-[var(--color-gray-75-dk)] dark:to-[var(--color-gray-100-dk)] animate-pulse" />
                      <div className="h-[14px] w-[50px] rounded bg-gradient-to-r from-[var(--color-gray-75)] to-[var(--color-gray-100)] dark:from-[var(--color-gray-75-dk)] dark:to-[var(--color-gray-100-dk)] animate-pulse" />
                    </div>
                  </div>
                ))
              : metricsData?.cards?.map((card, index) => (
                  <div
                    key={index}
                    className="flex flex-col gap-[5px] items-center justify-center"
                  >
                    <div className="flex flex-col gap-[7px] items-center">
                      <p className="text-xxs font-bold uppercase text-verba-indigo dark:text-verba-indigo-dk leading-none">
                        {card.primary_label}
                      </p>
                      <p className="text-3xl font-semibold text-verba-indigo dark:text-verba-indigo-dk leading-none">
                        {card.primary_value}
                      </p>
                    </div>
                    {card.secondary_label && (
                      <div className="flex flex-col gap-[2px] items-center">
                        <p className="text-xs font-medium text-analytics-gray dark:text-analytics-gray-dk leading-[1.3] text-center">
                          {card.secondary_label}
                        </p>
                        {card.secondary_value && (
                          <p className="text-xs font-medium text-analytics-gray dark:text-analytics-gray-dk leading-[1.3] text-center">
                            {card.secondary_value}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
          </div>
        </div>
      )}
    </div>
  );
};
